import { gsap } from "gsap";
import { DropShadowFilter, GlowFilter } from "pixi-filters";
import { IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { GameFont } from "../../../../common/GameFont";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { DynamicBackground } from "../components/DynamicBackground";
import { TopPanel } from "../../../../game/components/toppanel/TopPanel";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { ICheckoutHourlyBonusController } from "../controller/ICheckoutHourlyBonusController";
import { CancellationToken } from "async/CancellationToken";
import { CheckoutHourlyBonusHand } from "../components/CheckoutHourlyBonusHand";
import { CheckoutHourlyBonusWheelTooltip } from "../components/CheckoutHourlyBonusWheelTooltip";
import { CheckoutHourlyBonusTooltip } from "../components/CheckoutHourlyBonusTooltip";
import { CheckoutHourlyBonusResourcePackage } from "../resources/CheckoutHourlyBonusResourcePackage";
import { DicesResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/DicesResourcePackage";
import { CheckoutsTooltipResourcePackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CheckoutsTooltipResourcePackage";

export class CheckoutHourlyBonusPopupView extends BaseScalableView<void> {
    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly dicesResources: DicesResourcePackage;
    private readonly checkoutHourlyBonusController: ICheckoutHourlyBonusController;
    private readonly isWheel: boolean;
    private readonly topPanel: TopPanel;
    private readonly checkoutHourlyBonusResources: CheckoutHourlyBonusResourcePackage;
    private readonly checkoutsTooltipResources: CheckoutsTooltipResourcePackage;

    private viewBackground: Sprite;
    private dynamicBackground: DynamicBackground;
    private tooltipWheel: CheckoutHourlyBonusWheelTooltip;
    private tooltip: CheckoutHourlyBonusTooltip;
    private collectButton: ButtonWithScaleAnimation;
    private hand: CheckoutHourlyBonusHand;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        dicesResources: DicesResourcePackage,
        checkoutHourlyBonusResources: CheckoutHourlyBonusResourcePackage,
        checkoutsTooltipResources: CheckoutsTooltipResourcePackage,
        canvasScaler: ICanvasScaler,
        checkoutHourlyBonusController: ICheckoutHourlyBonusController,
        isWheel: boolean,
        topPanel: TopPanel
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.dicesResources = dicesResources;
        this.checkoutHourlyBonusResources = checkoutHourlyBonusResources;
        this.checkoutsTooltipResources = checkoutsTooltipResources;
        this.checkoutHourlyBonusController = checkoutHourlyBonusController;
        this.isWheel = isWheel;
        this.topPanel = topPanel;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public start(): void { };

    private onCancellationRequested(): void {
        this.operationControls.complete();
        this.destroy();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.addChild(this.viewBackground);
        this.dynamicBackground = new DynamicBackground(this.commonResources, this.dicesResources, this.checkoutHourlyBonusResources, this.isWheel, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.dynamicBackground);
        if (this.isWheel) {
            this.tooltipWheel = new CheckoutHourlyBonusWheelTooltip(this.checkoutsTooltipResources);
            this.addChild(this.tooltipWheel);
        } else {
            this.tooltip = new CheckoutHourlyBonusTooltip(this.checkoutsTooltipResources);
            this.addChild(this.tooltip);
        }
        const glowFilterWhite = new GlowFilter({ distance: 15, outerStrength: 3, innerStrength: 0, color: 0xFFFFFF, quality: 0.1, alpha: 1 });
        const glowFilterPink = new GlowFilter({ distance: 40, outerStrength: 2, innerStrength: 0, color: 0xEB00FF, quality: 0.1, alpha: 0.5 });
        const shadowFilterPink = new DropShadowFilter({ offset: new Point(0, 0), blur: 40, color: 0xEE85FF, quality: 4, alpha: 0.5 });
        const shadowFilterPurple = new DropShadowFilter({ offset: new Point(0, 0), blur: 60, color: 0xFF50ED, quality: 4, alpha: 0.3 });
        let buttonTexture = this.checkoutHourlyBonusResources.assets_views_checkout_hourly_bonus_gift_dices_png;
        if (this.isWheel) {
            buttonTexture = this.checkoutHourlyBonusResources.assets_views_checkout_hourly_bonus_gift_wheel_png;
        }
        this.collectButton = new ButtonWithScaleAnimation(buttonTexture);
        this.collectButton.onUp.add(this.onCollectButtonClick, this);
        this.addChild(this.collectButton);
        const collectButtonText = new Text('COLLECT', new CollectButtonTextStyle());
        collectButtonText.position.set(12, this.collectButton.height + 8);
        this.collectButton.addChild(collectButtonText);
        this.collectButton.filters = [glowFilterWhite, glowFilterPink, shadowFilterPink, shadowFilterPurple];
        this.hand = new CheckoutHourlyBonusHand(this.commonResources);
        this.addChild(this.hand);
    }

    private layout(width: number, height: number): void {
        this.dynamicBackground.position.set(width * 0.5, height * 0.5);
        if (this.isWheel) {
            this.tooltipWheel.position.set(width * 0.5, height * 0.53 + this.tooltipWheel.height * 0.5);
        } else {
            this.tooltip.position.set(width * 0.5, height * 0.50 + this.tooltip.height * 0.5);
        }
        this.collectButton.position.set(667, this.topPanel.y + this.topPanel.height + 30);
        this.hand.scale.set(0.75, -0.75);
        this.hand.position.set(665, this.collectButton.y + 440);
    }

    private async onCollectButtonClick(): Promise<void> {
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        hideAnimation.play();
        hideAnimation.then(() => hideAnimation.kill());
        await this.checkoutHourlyBonusController.showFortuneWheelScene(this.cancellationToken);
        this.operationControls.complete();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.collectButton.onUp.remove(this.onCollectButtonClick, this);
        super.destroy(options);
    }
}

class CollectButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 10,
            dropShadowBlur: 10,
            dropShadowColor: "#ffffff",
            dropShadowDistance: 0,
            fill: "#253480",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42
        }));
    }
}
